import { createAction } from '@reduxjs/toolkit';
import debounceAction from 'debounce-action';

import { apologise } from './apology.thunks';
import { getApiConf } from './subscriptions.thunks';

import { systemDeletion } from '../../events/pipeDeletion.event';

import { SystemsAPI } from '../../api/systems.api';

import type { AppThunk } from 'Types/state.types';
import type { System } from 'Types/system.types';
import type { BatchDeletionResponse, SomeObject } from 'Types/common.types';

export const systemAdded = createAction<System>('systems/added');
export const systemsAdded = createAction<System[]>('systems/multiple-added');
export const systemRemoved = createAction<string[]>('systems/removed');
export const systemsDeleted = createAction<System[]>('systems/deleted');
export const systemUpdated = createAction<System>('system/updated');
export const systemsUpdated = createAction<System[]>('systems/updated');
export const systemLoaded = createAction<{ id: string; system: System }>('system/loaded');

export const systemLoadAllStarted = createAction('systems/loadAllStarted');
export const systemLoadAllFinished = createAction<System[]>('systems/loadAllFinished');
export const systemLoadAllFailed = createAction('systems/loadAllFailed');

export const systemSecretsLoaded = createAction<{
  id: string;
  secrets: string[];
}>('system-secrets/loadAll');
export const systemSecretsAdded = createAction<{
  id: string;
  secrets: string[];
}>('system-secrets/added');
export const systemSecretsRemoved = createAction<{
  id: string;
  secretName: string;
}>('system-secrets/remove');

export const SystemActions = {
  add(newSystem: System): AppThunk {
    return function (dispatch, getState) {
      return SystemsAPI.add(getApiConf(getState), newSystem)
        .then((systems) => dispatch(systemAdded(systems[0])))
        .catch((error) => dispatch(apologise(error)));
    };
  },

  delete(systemIds: string[]): AppThunk {
    return function (dispatch, getState) {
      const numberOfItems = systemIds.length;
      systemDeletion.emit({ numberOfItems, status: 'start-api-delete' });

      return SystemsAPI.remove(getApiConf(getState), systemIds)
        .then((batchDeletionResponse: BatchDeletionResponse) => {
          systemDeletion.emit({ numberOfItems, status: 'finish-api-delete-request' });

          const isDeleted =
            batchDeletionResponse?.deleted && batchDeletionResponse?.deleted.length > 0;

          const isPartiallyDeleted =
            (batchDeletionResponse?.forbidden && batchDeletionResponse?.forbidden.length > 0) ||
            (batchDeletionResponse?.not_found && batchDeletionResponse.not_found.length > 0);

          if (!isDeleted) {
            systemDeletion.emit({ numberOfItems, status: 'deletion-failed' });
            dispatch(
              apologise(
                'Failed to delete selected systems. Please try again or contact support if the issue persists'
              )
            );
          } else {
            systemDeletion.emit({ numberOfItems, status: 'start-post-delete-actions' });

            if (batchDeletionResponse?.deleted) {
              dispatch(systemRemoved(batchDeletionResponse?.deleted));
            }

            if (isPartiallyDeleted) {
              dispatch(
                apologise(
                  'Delete selected systems partially successful. Please try again or contact support if the issue persists'
                )
              );
            }

            systemDeletion.emit({ numberOfItems, status: 'finish-post-delete-actions' });
          }
        })
        .catch((error) => {
          dispatch(apologise(error));
          systemDeletion.emit({ numberOfItems, status: 'finish-api-with-error' });
        });
    };
  },

  update(id: string, updatedSystem: System): AppThunk {
    return function (dispatch, getState) {
      return SystemsAPI.update(getApiConf(getState), id, updatedSystem)
        .then((system) => dispatch(systemUpdated(system)))
        .catch((error) => dispatch(apologise(error)));
    };
  },

  load: debounceAction(
    (id: string): AppThunk =>
      (dispatch, getState) => {
        return SystemsAPI.get(getApiConf(getState), id)
          .then((system) => dispatch(systemLoaded({ id, system })))
          .catch((error) => dispatch(apologise(error)));
      },
    1000,
    { leading: true, trailing: false }
  ),

  loadForced:
    (id: string): AppThunk =>
    (dispatch, getState) => {
      return SystemsAPI.get(getApiConf(getState), id)
        .then((system) => dispatch(systemLoaded({ id, system })))
        .catch((error) => dispatch(apologise(error)));
    },

  loadAll: debounceAction(
    (loadInternal = false): AppThunk =>
      (dispatch, getState) => {
        dispatch(systemLoadAllStarted());
        return SystemsAPI.getAll(getApiConf(getState), loadInternal)
          .then((systems) => dispatch(systemLoadAllFinished(systems)))
          .catch((error) => {
            dispatch(systemLoadAllFailed());
            dispatch(apologise(error));
          });
      },
    1000,
    { leading: true, trailing: false }
  ),

  loadAllForced:
    (loadInternal = false): AppThunk =>
    (dispatch, getState) => {
      dispatch(systemLoadAllStarted());
      return SystemsAPI.getAll(getApiConf(getState), loadInternal)
        .then((systems) => dispatch(systemLoadAllFinished(systems)))
        .catch((error) => {
          dispatch(systemLoadAllFailed());
          dispatch(apologise(error));
        });
    },

  // TODO: IS-6787 This is not really a Redux action; it's dispatched as one so it can
  // access `getState()`. Should rethink this.
  getSinkPrototypes(systemId: string): AppThunk {
    return function (dispatch, getState) {
      return SystemsAPI.getSinkPrototypes(getApiConf(getState), systemId).catch((error) =>
        dispatch(apologise(error))
      );
    };
  },

  // TODO: IS-6787 This is not really a Redux action; it's dispatched as one so it can
  // access `getState()`. Should rethink this.
  getSourcePrototypes(systemId: string): AppThunk {
    return function (dispatch, getState) {
      return SystemsAPI.getSourcePrototypes(getApiConf(getState), systemId);
    };
  },

  // TODO: IS-6787 This is not really a Redux action; it's dispatched as one so it can
  // access `getState()`. Should rethink this.
  getSystemPrototypes(): AppThunk {
    return function (dispatch, getState) {
      return SystemsAPI.getSystemPrototypes(getApiConf(getState));
    };
  },

  /* System bound secrets */
  systemSecretsLoadAll: debounceAction(
    (id: string): AppThunk =>
      (dispatch, getState) => {
        return SystemsAPI.getSecrets(getApiConf(getState), id)
          .then((secrets) => dispatch(systemSecretsLoaded({ id, secrets })))
          .catch((error) => dispatch(apologise(error)));
      },
    1000,
    { leading: true, trailing: false }
  ),

  systemSecretsAdd(id: string, secrets: SomeObject[]): AppThunk {
    return (dispatch, getState) => {
      return SystemsAPI.addSecrets(getApiConf(getState), id, secrets)
        .then(() => dispatch(systemSecretsAdded({ id, secrets: Object.keys(secrets) })))
        .catch((error) => dispatch(apologise(error)));
    };
  },

  systemSecretDelete(id: string, secretName: string): AppThunk {
    return (dispatch, getState) => {
      return SystemsAPI.removeSecret(getApiConf(getState), id, secretName)
        .then(() =>
          dispatch(
            systemSecretsRemoved({
              id,
              secretName,
            })
          )
        )
        .catch((error) => dispatch(apologise(error)));
    };
  },
};
